import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query'
import { skillsApi } from '@/api/skills'
import { skillAttachmentsApi } from '@/api/skillAttachments'
import { useTenantKey } from './useTenantKey'
import type { CreateSkillRequest, UpdateSkillRequest, CreateSkillVersionRequest, SkillDetail } from '@/types'

export function useSkills(projectId: string) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'skills'),
    queryFn: () => skillsApi.getAll(projectId),
    enabled: !!projectId,
  })
}

export function useSkill(projectId: string, skillId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'skills', skillId ?? ''),
    queryFn: () => skillsApi.getById(projectId, skillId!),
    enabled: !!projectId && !!skillId,
  })
}

export function useSkillDetails(projectId: string, skillIds: string[]) {
  const tenantKey = useTenantKey()
  return useQueries({
    queries: skillIds.map(skillId => ({
      queryKey: tenantKey('projects', projectId, 'skills', skillId),
      queryFn: () => skillsApi.getById(projectId, skillId),
      enabled: !!projectId,
    })),
  })
}

export function useSkillVersions(projectId: string, skillId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'skills', skillId ?? '', 'versions'),
    queryFn: () => skillsApi.getVersions(projectId, skillId!),
    enabled: !!projectId && !!skillId,
  })
}

export function useSkillAttachments(projectId: string, skillId: string | undefined) {
  const tenantKey = useTenantKey()
  return useQuery({
    queryKey: tenantKey('projects', projectId, 'skills', skillId ?? '', 'attachments'),
    queryFn: () => skillAttachmentsApi.getBySkill(projectId, skillId!),
    enabled: !!projectId && !!skillId,
  })
}

export function useCreateSkill(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (data: CreateSkillRequest) => skillsApi.create(projectId, data),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'skills') })
    },
  })
}

export function useCreateSkillWithAttachments(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: async ({ request, attachments }: { request: CreateSkillRequest; attachments: File[] }) => {
      const created = await skillsApi.create(projectId, request)
      let attachmentsError: unknown = null
      if (attachments.length > 0) {
        try {
          await skillAttachmentsApi.upload(projectId, created.skillId, attachments)
        } catch (error) {
          attachmentsError = error
        }
      }
      return { ...created, attachmentsError }
    },
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'skills') })
    },
  })
}

export function useUpdateSkill(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateSkillRequest }) =>
      skillsApi.update(projectId, id, data),
    onSuccess: (_result, { id, data }) => {
      queryClient.setQueryData<SkillDetail>(
        tenantKey('projects', projectId, 'skills', id),
        previous => previous && { ...previous, displayName: data.displayName, description: data.description ?? null },
      )
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'skills') })
    },
  })
}

export function useDeleteSkill(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (id: string) => skillsApi.delete(projectId, id),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'skills') })
    },
  })
}

export function useCreateSkillVersion(projectId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreateSkillVersionRequest }) =>
      skillsApi.createVersion(projectId, id, data),
    onSuccess: (result, { id, data }) => {
      queryClient.setQueryData<SkillDetail>(
        tenantKey('projects', projectId, 'skills', id),
        previous => previous && {
          ...previous,
          content: data.content,
          latestVersion: {
            id: result.skillVersionId,
            skillId: id,
            version: result.version,
            contentSnapshot: data.content,
            contentHash: previous.latestVersion?.contentHash ?? '',
            createdAt: new Date().toISOString(),
          },
        },
      )
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'skills') })
    },
  })
}

export function useUploadSkillAttachments(projectId: string, skillId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (files: File[]) => skillAttachmentsApi.upload(projectId, skillId, files),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'skills') })
    },
  })
}

export function useDeleteSkillAttachment(projectId: string, skillId: string) {
  const queryClient = useQueryClient()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: (attachmentId: string) => skillAttachmentsApi.delete(projectId, skillId, attachmentId),
    onSuccess: () => {
      return queryClient.invalidateQueries({ queryKey: tenantKey('projects', projectId, 'skills') })
    },
  })
}

export function useSkillAttachmentDownloadUrl(projectId: string, skillId: string) {
  return useMutation({
    mutationFn: (attachmentId: string) => skillAttachmentsApi.getDownloadUrl(projectId, skillId, attachmentId),
    onSuccess: (url) => {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
  })
}

export function useLoadSkillForCopy(projectId: string, skillId: string) {
  const tenantKey = useTenantKey()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      queryClient.fetchQuery({
        queryKey: tenantKey('projects', projectId, 'skills', skillId),
        queryFn: () => skillsApi.getById(projectId, skillId),
        staleTime: 0,
      }),
  })
}
